import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError } from '@/lib/api';
import { listMatters } from '@/lib/api-client';
import type { MatterDetail } from '@/lib/types';

const ACTIVE_MATTER_KEY = 'cc_active_matter_id';

interface MatterContextValue {
  /** Loaded matters owned by the user. Excludes archived by default. */
  matters: MatterDetail[];
  /** Currently selected matter id. Persisted in localStorage. */
  activeMatterId: string | null;
  /** Active matter object, or null if not loaded or no selection. */
  activeMatter: MatterDetail | null;
  /** True while the initial fetch is in flight. */
  loading: boolean;
  /** Error from the last load attempt, if any. */
  error: string | null;
  setActiveMatterId: (id: string | null) => void;
  refresh: () => Promise<void>;
}

const MatterContext = createContext<MatterContextValue | null>(null);

interface MatterProviderProps {
  children: ReactNode;
}

export function MatterProvider({ children }: MatterProviderProps) {
  const [matters, setMatters] = useState<MatterDetail[]>([]);
  const [activeMatterId, setActiveMatterIdState] = useState<string | null>(() => readActiveId());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await listMatters();
      setMatters(list);
      // If the persisted active id is no longer in the list (deleted or
      // archived), drop it so the operator picks a fresh one.
      setActiveMatterIdState((current) => {
        if (current && !list.some((m) => m.id === current)) {
          writeActiveId(null);
          return null;
        }
        return current;
      });
    } catch (err) {
      // 401 means the user is not signed in; AuthProvider redirects shortly.
      if (err instanceof ApiError && err.status === 401) {
        setMatters([]);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load matters');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setActiveMatterId = useCallback((id: string | null) => {
    writeActiveId(id);
    setActiveMatterIdState(id);
  }, []);

  const activeMatter = useMemo(
    () => matters.find((m) => m.id === activeMatterId) ?? null,
    [matters, activeMatterId],
  );

  const value: MatterContextValue = {
    matters,
    activeMatterId,
    activeMatter,
    loading,
    error,
    setActiveMatterId,
    refresh,
  };

  return <MatterContext.Provider value={value}>{children}</MatterContext.Provider>;
}

export function useMatters(): MatterContextValue {
  const ctx = useContext(MatterContext);
  if (!ctx) {
    throw new Error('useMatters must be used inside <MatterProvider>');
  }
  return ctx;
}

// --- localStorage helpers ---------------------------------------------------

function readActiveId(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_MATTER_KEY);
  } catch {
    return null;
  }
}

function writeActiveId(id: string | null): void {
  try {
    if (id === null) {
      window.localStorage.removeItem(ACTIVE_MATTER_KEY);
    } else {
      window.localStorage.setItem(ACTIVE_MATTER_KEY, id);
    }
  } catch {
    // localStorage may be unavailable in private browsing or sandboxed contexts.
  }
}
